import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from "@angular/common";
import {Subscription} from "rxjs";
import {
  AppService,
  Cart,
  CartEntry,
  CartService,
  CheckoutSteps,
  DiscountEntry,
  format_time,
  formatAddress,
  getStepRoute,
  Host,
  Menu,
  OrderMethod,
  relative_time,
  Table,
  TableType
} from "../../../../../common";

@Component({
  selector: 'checkout',
  standalone: false,
  templateUrl: './checkout.html',
  styleUrls: ['./checkout.scss']
})
export class CheckoutPage implements OnInit, OnDestroy {

  host: Host;
  table: Table;
  menu: Menu;
  cart: Cart;
  activeEntry: CartEntry = null;
  takesOrders: boolean = false;
  addressStr: string;
  dayStr: string = "";
  timeStr: string = "";
  estimatedArrivalTime: string = "";
  TableType = TableType;
  OrderMethod = OrderMethod;

  private subscriptions: Array<Subscription> = new Array<Subscription>();

  constructor(
    private router: Router,
    private location: Location,
    private appService: AppService,
    private cartService: CartService
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.appService.load().subscribe(({host, menu, table, cart}) => {
        this.host = host;
        this.menu = menu;
        this.table = table;
        this.cart = cart;
        if (this.cart.method == OrderMethod.inhouse) {
          this.takesOrders = this.table.is_active;
        } else {
          this.takesOrders = this.host.has_takeout || this.host.has_delivery;
        }
        this.addressStr = formatAddress(host, cart.address);
        const preorderTime = cart.getDeliveryTime();
        this.estimatedArrivalTime = relative_time(preorderTime.dt, this.host, true);
        if (cart.desired_time > 0) {
          if (preorderTime.time != null) {
            this.dayStr = preorderTime.day_label;
            this.timeStr = format_time(preorderTime.time);
          }
        }

        if (this.cart.positions.length == 0) {
          this.router.navigate(['/'])
        }

        if (!this.cart.isValidated) {
          this.cart.validate(true).then((result) => {
            this.gotoNextStepIfNecessary();
          });
        } else {
          this.gotoNextStepIfNecessary();
        }
      })
    );
  }

  gotoNextStepIfNecessary(): void {
        let next = this.cart.getNextCheckoutStep();
        if (next != CheckoutSteps.review) {
          this.router.navigate(getStepRoute(next), {replaceUrl: true});
        }
  }

  ngOnDestroy(): void {
    let sub = null;
    while (sub = this.subscriptions.pop()) {
      sub.unsubscribe();
    }
  }

  onSelect(entry: CartEntry): void {
    this.activeEntry = entry;
  }

  addOne($event: MouseEvent, entry: CartEntry): void {
    entry.count += 1;
    this.cartService.saveCartToStorage(this.cart);
    $event.stopPropagation();
  }

  removeOne($event: MouseEvent, entry: CartEntry): void {
    entry.count -= 1;
    this.cartService.saveCartToStorage(this.cart);
    $event.stopPropagation();
  }

  removeAll($event: MouseEvent, entry: CartEntry): void {
    this.cart.positions = this.cart.positions.filter(x => x != entry);
    this.cartService.saveCartToStorage(this.cart);

  }

  unselect($event): void {
    this.activeEntry = null;
    $event.stopPropagation();
  }

  onDiscountClick(entry: DiscountEntry): void {
    if (entry.needsSelection()) {
      // reset selection(s), and call benefit selection page
      entry.discount.benefits.filter(b => b.needsSelection()).forEach(b => {
        const selection = this.cart.benefitSelections.get(b.id.toString() + '-' + entry.index.toString());
        if (selection) {
          selection.variant = null;
        }
      });
      this.router.navigate(getStepRoute(CheckoutSteps.selectBenefit));
    }
  }

  onContactClick(): void {
    this.router.navigate(getStepRoute(CheckoutSteps.enterContact));
  }

  onDeliveryClick(): void {
    if (this.host.has_delivery) {
      this.router.navigate(getStepRoute(CheckoutSteps.chooseMethod));
    }
  }

  onTimeClick(): void {
    this.router.navigate(getStepRoute(CheckoutSteps.chooseTime));
  }

  onPaymentClick(): void {
    this.router.navigate(getStepRoute(CheckoutSteps.choosePayment));
  }

  back(): void {
    this.location.back()
  }

  canProceed(): boolean {
    return this.takesOrders && this.cart.getCount() > 0
  }

  getNextStep(): CheckoutSteps {
    let next = this.cart.getNextCheckoutStep();
    if (next == CheckoutSteps.review) {
      next = (this.cartService.isDemo) ? CheckoutSteps.submitDemo : CheckoutSteps.submitOrder;
    }
    return next;
  }

  next(): boolean {
    if (this.cart.getCount() > 0) {
      const next = this.getNextStep();
      // fix cart.method, unless we are choosing it in the next step
      // TODO: I don't think this is necessary at all, but I'm not sure
      //       We definitely need unittests to cover all cases
      if (next != CheckoutSteps.chooseMethod) {
        if (this.cart.method == null) {
          this.cart.method = this.table.type as string as OrderMethod;
          if (!this.host.has_delivery && this.table.type == TableType.delivery) {
            // we are on a delivery table, but only takeout is supported
            this.cart.method = OrderMethod.takeout;
          }
        }
      }
      this.cartService.saveCartToStorage(this.cart);
      this.router.navigate(getStepRoute(next));
    }
    return false;
  }

}
